/* eslint-disable react-hooks/exhaustive-deps */
import React, { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { AccessibilityToResponse } from 'src/constants/accessibilityToResponse';

import type { HNSkjemaFrontend } from '../../types/Resources';

import Button from '@helsenorge/designsystem-react/components/Button';
import { Icon } from '@helsenorge/designsystem-react/components/Icon';
import ChevronLeft from '@helsenorge/designsystem-react/components/Icons/ChevronLeft';
import HelpSign from '@helsenorge/designsystem-react/components/Icons/HelpSign';
import X from '@helsenorge/designsystem-react/components/Icons/X';
import { isMobileUA } from '@helsenorge/designsystem-react/utils/mobile';

import { theme, useFocusTrap } from '@helsenorge/designsystem-react';
import SafeHTML from '@helsenorge/framework-utils/components/safe-html';
import { link as proxyLink } from '@helsenorge/framework-utils/hn-proxy-service';
import { setAllowNavigation } from '@helsenorge/framework-utils/hn-user';
import type { SidebarItem } from '@helsenorge/refero';

import Seksjon, { type Props as SeksjonProps } from './seksjon';

export interface SidebarProps {
  resources: HNSkjemaFrontend;
  isOpen?: boolean;
  toggleShowSidebar: (e: Event | FormEvent<unknown> | undefined) => void;
  hideSidebar: (e: Event | FormEvent<unknown> | undefined) => void;
  showSidebar: (e: Event | FormEvent<unknown> | undefined) => void;
  kanLagres: boolean;
  kanSendes: boolean;
  harRepresentasjonsMulighet: boolean;
  accessibilityToResponse?: AccessibilityToResponse;
  sidebarData: Array<SidebarItem>;
  formTitle: string;
  printReference?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  resources,
  isOpen = false,
  toggleShowSidebar,
  hideSidebar,
  showSidebar,
  kanLagres,
  kanSendes,
  harRepresentasjonsMulighet,
  accessibilityToResponse,
  sidebarData,
  formTitle,
  printReference,
}) => {
  const [disableTransition, setDisableTransition] = useState<boolean>(false);

  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const helpTitleRef = useRef<HTMLHeadingElement | null>(null);
  const isOpenRef = useRef<boolean>(isOpen);

  useFocusTrap(sidebarRef as React.RefObject<HTMLDivElement>, isOpen);

  const prevIsOpenRef = useRef<boolean>(isOpen);
  useEffect(() => {
    prevIsOpenRef.current = isOpen;
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // ---------- helpers ----------
  const wrapSidebarTekst = useCallback((tekst: string) => `${tekst}\n\n`, []);

  const generateSectionContent = useCallback(
    (header: string, content: Array<string>): SeksjonProps => ({
      header,
      content: content.map((x: string, index: number) => <SafeHTML key={index} html={x} tagName="p" />),
    }),
    []
  );
  const isOpenInHistoryState = useCallback((): boolean => {
    return typeof window !== 'undefined' && !!(window.history.state && window.history.state.helpDrawerOpen);
  }, []);

  const isLightboxOpen = useCallback((): boolean => {
    if (typeof document === 'undefined') return false;
    return document.querySelectorAll('.toolkit-lightbox').length > 0;
  }, []);

  const removePreventscroll = useCallback(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('preventscroll');
    }
  }, []);

  const setScroll = useCallback(() => {
    if (typeof document === 'undefined') return;
    if (document.body.classList.contains('preventscroll') && !isOpen && !isLightboxOpen()) {
      removePreventscroll();
    } else {
      document.body.classList.add('preventscroll');
    }
  }, [isOpen, isLightboxOpen, removePreventscroll]);

  const open = useCallback(() => {
    if (typeof window !== 'undefined' && isMobileUA() && !isOpenInHistoryState()) {
      window.history.pushState(Object.assign({}, window.history.state, { helpDrawerOpen: true }), '', window.location.href);
    }
    setScroll();
  }, [isOpenInHistoryState, setScroll]);

  const close = useCallback(() => {
    if (typeof window !== 'undefined' && isMobileUA() && isOpenInHistoryState()) {
      setAllowNavigation(true);
      window.history.back();
    }
    if (!isLightboxOpen()) {
      removePreventscroll();
    }
  }, [isOpenInHistoryState, isLightboxOpen, removePreventscroll]);

  const disableTransitionToPreventDoubleAnimation = useCallback((callback: () => void) => {
    setDisableTransition(true);
    callback();
    const t = window.setTimeout(() => setDisableTransition(false), 500);
    return (): void => window.clearTimeout(t);
  }, []);

  const elementIsInSidebarLink = useCallback((targets: EventTarget[]): boolean => {
    for (const target of targets) {
      if ((target as HTMLElement)?.classList?.contains('sidebar-link')) return true;
    }
    return false;
  }, []);

  const elementIsInHelpDrawer = useCallback((targets: EventTarget[]): boolean => {
    for (const target of targets) {
      if ((target as HTMLElement)?.classList?.contains('skjemasidebar')) return true;
    }
    return false;
  }, []);

  const seksjonsData = useMemo(() => {
    const seksjonerFraSkjema: { [key: string]: Array<string> } = {
      'SOT-1': [],
      'SOT-2': [],
      'SOT-3': [],
    };

    sidebarData.forEach((x: SidebarItem) => {
      if (x.item.code && x.item.code.length > 0 && x.item.code[0].code) {
        if (seksjonerFraSkjema[x.item.code[0].code]) {
          seksjonerFraSkjema[x.item.code[0].code].push(x.markdownText);
        }
      }
    });

    if (harRepresentasjonsMulighet) {
      seksjonerFraSkjema['SOT-1'].push(wrapSidebarTekst(resources.sidebarSectionText_Alternativer_Representasjon));
    }

    seksjonerFraSkjema['SOT-2'].push(wrapSidebarTekst(resources.sidebarSectionText_Veiledning_KontaktHelsenorge));

    if (accessibilityToResponse === AccessibilityToResponse.Recipient) {
      seksjonerFraSkjema['SOT-2'].push(wrapSidebarTekst(resources.sidebarSectionText_Veiledning_InnsendingUtenLagring));
    } else {
      if (kanLagres) {
        seksjonerFraSkjema['SOT-2'].push(
          wrapSidebarTekst(
            kanSendes ? resources.sidebarSectionText_Veiledning_LagringInnboks : resources.sidebarSectionText_Veiledning_LagringDokument
          )
        );
      } else {
        seksjonerFraSkjema['SOT-2'].push(wrapSidebarTekst(resources.sidebarSectionText_Veileder_Opplysninger_KontaktHelsenorge));
      }
    }

    if (printReference) {
      const printName = `${resources.sidebar_printLink} ${formTitle}`;
      const printUrl = proxyLink('skjemainternal', 'api/v1/Binary', {
        id: printReference.split('/')[1],
      });
      seksjonerFraSkjema['SOT-1'].push(
        wrapSidebarTekst(resources.sidebarSectionText_Alternativer_LoggInn.replace('{0}', `<a href="${printUrl}">${printName}</a>`))
      );
    }

    return [
      generateSectionContent(resources.sidebarSectionHeader_Alternativer, seksjonerFraSkjema['SOT-1']),
      generateSectionContent(resources.sidebarSectionHeader_Veiledning, seksjonerFraSkjema['SOT-2']),
      generateSectionContent(resources.sidebarSectionHeader_BehandlingMottaker, seksjonerFraSkjema['SOT-3']),
    ];
  }, [
    resources,
    sidebarData,
    harRepresentasjonsMulighet,
    kanLagres,
    kanSendes,
    printReference,
    formTitle,
    wrapSidebarTekst,
    generateSectionContent,
  ]);

  const toggleFocus = useCallback(() => {
    if (isOpen) {
      if (helpTitleRef.current) {
        helpTitleRef.current.focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    toggleFocus();

    const popStateListener = (e: PopStateEvent): void => {
      setAllowNavigation(false);
      const helpDrawerOpen = !!(window.history.state && window.history.state.helpDrawerOpen);
      if (helpDrawerOpen && !isOpenRef.current) {
        disableTransitionToPreventDoubleAnimation(() => showSidebar(e));
      } else if (!helpDrawerOpen && isOpenRef.current) {
        disableTransitionToPreventDoubleAnimation(() => hideSidebar(e));
      }
    };

    const clickListener = (e: MouseEvent): void => {
      if (!isOpenRef.current) return;

      const path =
        (e.composedPath && e.composedPath()) ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((e as any).path as EventTarget[] | undefined) ||
        (function buildPath(node: EventTarget | null): EventTarget[] {
          const acc: EventTarget[] = [];
          while (node && (node as Node).parentNode) {
            acc.push(node);
            node = (node as Node).parentNode;
          }
          return acc;
        })(e.target as EventTarget);

      const targets = Array.isArray(path) ? path : [];

      if (!elementIsInHelpDrawer(targets) && !elementIsInSidebarLink(targets)) {
        hideSidebar(e);
      }
    };

    window.addEventListener('popstate', popStateListener);
    window.addEventListener('click', clickListener);

    return (): void => {
      window.removeEventListener('popstate', popStateListener);
      window.removeEventListener('click', clickListener);
    };
  }, []);

  useEffect(() => {
    if (isOpen && prevIsOpenRef.current !== isOpen) {
      toggleFocus();
    }

    if (!prevIsOpenRef.current && isOpen) {
      open();
    } else if (prevIsOpenRef.current && !isOpen) {
      close();
    }
  }, [isOpen, toggleFocus, open, close]);

  useEffect(() => {
    return (): void => {
      removePreventscroll();
    };
  }, [removePreventscroll]);

  const helpDrawerClasses = classNames({
    skjemasidebar: true,
    open: isOpen,
  });

  const style: React.CSSProperties = disableTransition ? { transitionDuration: '0s' } : {};

  return (
    <div className={helpDrawerClasses} style={style} data-testid="sidebar" ref={sidebarRef}>
      <span className="skjemasidebar__control">
        <button
          data-testid="sidebar-toggle"
          className="skjemasidebar__controlbutton"
          type="button"
          onClick={toggleShowSidebar}
          tabIndex={0}
          aria-label={isOpen ? resources.sidebar_closelabel : resources.sidebar_openlabel}
        >
          <Icon className="icon_question_sidebar" svgIcon={HelpSign} color={theme.palette.white} />
          <Icon className="icon_arrow_sidebar" svgIcon={ChevronLeft} color={theme.palette.white} />
        </button>
      </span>

      <div className="skjemasidebar__contents" aria-hidden={!isOpen}>
        <div className="skjemasidebar__drawerheader">
          <div
            className="skjemasidebar__title-wrap"
            role="heading"
            aria-level={1}
            aria-label={`${resources.sidebar_titlearia} ${resources.sidebar_titleSkjema}`}
            ref={helpTitleRef as React.RefObject<HTMLDivElement>}
          >
            <Icon svgIcon={HelpSign} color={theme.palette.white} />
            <h1 className="skjemasidebar__title">{resources.sidebar_titleSkjema}</h1>
          </div>
          <Button
            testId="hide-sidebar"
            onClick={e => hideSidebar(e as FormEvent<unknown>)}
            variant={'borderless'}
            ariaLabel={resources.sidebar_closelabel}
          >
            <Icon svgIcon={X} color={theme.palette.white} />
          </Button>
        </div>

        <div>
          {seksjonsData
            .filter(x => x.content.length > 0)
            .map(x => (
              <Seksjon key={x.header} header={x.header} content={x.content} />
            ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
